import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Calendar, MapPin, DollarSign, Loader2, Trophy } from "lucide-react";

function fmtDateShort(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return s; }
}

function DateBadge({ dateStr }) {
  if (!dateStr) return null;
  let d;
  try { d = new Date(dateStr + "T00:00:00"); } catch { return null; }
  const month = d.toLocaleDateString(undefined, { month: "short" }).toUpperCase();
  const day = d.getDate();
  const year = d.getFullYear();
  return (
    <div className="shrink-0 w-20 rounded-2xl bg-white border-2 border-[#FF9F1C] shadow-[0_6px_16px_rgb(255,159,28,0.15)] overflow-hidden text-center">
      <div className="bg-[#FF9F1C] text-white text-[10px] font-black uppercase tracking-widest py-1">{month}</div>
      <div className="font-display font-black text-3xl leading-none pt-2 text-[#1E1E24]">{day}</div>
      <div className="text-[10px] text-[#7A7D8C] pb-2 pt-1 font-semibold">{year}</div>
    </div>
  );
}

export default function Competitions() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events/public?kind=competition")
      .then((r) => setEvents(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-8">
        <span className="text-xs uppercase tracking-wider font-semibold text-[#FF9F1C]">Competitions</span>
        <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none" data-testid="competitions-heading">Youth Cubing Competitions</h1>
        <p className="mt-6 max-w-2xl text-lg text-[#7A7D8C]" data-testid="competition-format-summary">Select a competition below for event details.</p>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-24">
        {loading && (
          <div className="rounded-3xl border border-slate-100 bg-white p-16 text-center text-[#7A7D8C]" data-testid="comps-loading">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading competitions…
          </div>
        )}

        {!loading && events.length === 0 && (
          <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-white p-16 text-center" data-testid="comps-empty">
            <Trophy className="w-8 h-8 text-[#FF9F1C] mx-auto" />
            <h2 className="font-display font-black text-2xl">No upcoming competitions are currently scheduled</h2>
            <p className="mt-2 text-[#7A7D8C]">Upcoming competitions will be listed here as dates are confirmed.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_12px_30px_rgb(255,159,28,0.35)]">Get notified <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        )}

        {!loading && events.length > 0 && (
          <div className="grid gap-5 md:grid-cols-2" data-testid="comps-grid">
            {events.map((e) => <CompetitionCard key={e.id} event={e} />)}
          </div>
        )}

        <div className="mt-16 rounded-[36px] bg-[#FDE74C]/25 border border-[#FDE74C]/40 p-8 md:p-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-display font-black text-lg tracking-tight">Information for parents</h3>
            <p className="mt-4 text-[#1E1E24]/90 leading-relaxed" data-testid="competition-parent-information">Our competitions are open to students in grades 3–12. Organizers provide a supportive, structured environment with clear guidance throughout the event.</p>
          </div>
          <div className="rounded-3xl bg-white border border-slate-100 p-6">
            <h4 className="font-display font-bold text-xl">What to expect on event day</h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#FF9F1C] text-white grid place-items-center font-bold text-xs shrink-0">1</span> Check in with the QR code posted at the door.</li>
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#FF9F1C] text-white grid place-items-center font-bold text-xs shrink-0">2</span> One Timing Solve confirms the right category.</li>
              <li className="flex gap-3" data-testid="event-day-competition-step"><span className="w-6 h-6 rounded-full bg-[#FF9F1C] text-white grid place-items-center font-bold text-xs shrink-0">3</span> Complete one timed competition solve. This result determines placement within your category.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

function CompetitionCard({ event }) {
  return (
    <Link
      to={`/competitions/${event.id}`}
      data-testid={`comp-card-${event.id}`}
      className="group rounded-3xl bg-white border border-slate-100 p-6 md:p-7 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:border-[#FF9F1C] hover:shadow-[0_16px_40px_rgb(255,159,28,0.15)] hover:-translate-y-0.5 transition-all"
    >
      <div className="flex gap-5 items-start">
        <DateBadge dateStr={event.start_date} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-black text-[#FF9F1C]">Competition</div>
          <h2 className="mt-1 font-display font-black text-2xl tracking-tight leading-tight group-hover:text-[#FF9F1C] transition-colors">{event.title}</h2>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-[#7A7D8C]">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDateShort(event.start_date)}{event.end_date && event.end_date !== event.start_date ? ` – ${fmtDateShort(event.end_date)}` : ""}</span>
            {event.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {event.location}</span>}
            {event.registration_fee != null && <span className="inline-flex items-center gap-1 font-semibold text-[#B36906]"><DollarSign className="w-3.5 h-3.5" /> {event.registration_fee}</span>}
          </div>
          <div className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-[#FF9F1C] group-hover:gap-2 transition-all">
            View competition <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}
